import React, { useState } from 'react';
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Container,
} from 'react-bootstrap';
import { useGetExpensesQuery, useUpdateExpenseMutation } from '../../slices/expensesApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

// Utility: escape regex special characters
const escapeRegex = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const ExpenseSearchReplaceScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [replacementText, setReplacementText] = useState('');
  const [previewResults, setPreviewResults] = useState([]);
  const [isReplacing, setIsReplacing] = useState(false);

  const { pageNumber } = useParams();
  const page = Number(pageNumber) || 1;

  const { data: expensesData, isLoading, error } = useGetExpensesQuery({ pageNumber: page });
  const [updateExpense] = useUpdateExpenseMutation();

  // Preview matches in details or notes
  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const matches = expensesData?.expenses
      ?.filter(
        (exp) =>
          exp.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          exp.notes?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map((exp) => ({
        expenseId: exp._id,
        date: exp.date,
        details: exp.details,
        notes: exp.notes,
      }));

    setPreviewResults(matches || []);
  };

  // Replace spelling errors
  const handleReplace = async () => {
    if (!replacementText.trim()) return toast.error('Replacement text required');
    if (!searchTerm.trim()) return toast.error('Search term required');

    const affectedExpenses = expensesData?.expenses?.filter(
      (exp) =>
        exp.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exp.notes?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!affectedExpenses || affectedExpenses.length === 0) {
      return toast.info('No matching expenses found');
    }

    setIsReplacing(true);

    try {
      const regex = new RegExp(escapeRegex(searchTerm), 'gi');

      for (const exp of affectedExpenses) {
        const updatedDetails = exp.details ? exp.details.replace(regex, replacementText) : exp.details;
        const updatedNotes = exp.notes ? exp.notes.replace(regex, replacementText) : exp.notes;

        // ✅ Pass fields directly to match backend expectations
        await updateExpense({
          expenseId: exp._id,
          date: exp.date,
          account: exp.account,
          accountName: exp.accountName,
          subAccount: exp.subAccount,
          details: updatedDetails,
          amount: exp.amount,
          images: exp.images,
          category: exp.category,
          notes: updatedNotes,
          status: exp.status,
        }).unwrap();
      }

      toast.success(`Replaced "${searchTerm}" with "${replacementText}" in expenses`);
      setPreviewResults([]);
      setSearchTerm('');
      setReplacementText('');
    } catch (err) {
      toast.error(err?.data?.message || 'Update failed');
    } finally {
      setIsReplacing(false);
    }
  };

  // Highlight matches in preview
  const highlightText = (text) => {
    if (!searchTerm) return text;
    const regex = new RegExp(escapeRegex(searchTerm), 'gi');
    return text?.replace(regex, (match) => `<mark>${match}</mark>`);
  };

  return (
    <Container fluid>
      <Row>
        <Col md={12}>
          <h1>Search & Replace in Expenses</h1>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={5}>
          <Form.Control
            type="text"
            placeholder="Search for text in details/notes"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={5}>
          <Form.Control
            type="text"
            placeholder="Replacement text"
            value={replacementText}
            onChange={(e) => setReplacementText(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Button
            variant="primary"
            onClick={handleSearch}
            className="me-2"
            disabled={!searchTerm.trim() || isReplacing}
          >
            Preview Matches
          </Button>
          <Button
            variant="success"
            onClick={handleReplace}
            disabled={!replacementText.trim() || isReplacing}
          >
            Replace All
          </Button>
        </Col>
      </Row>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'Error loading expenses'}</Message>
      ) : previewResults.length === 0 ? (
        <Alert variant="info">No matching expenses found. Try a different search term.</Alert>
      ) : (
        <Table striped bordered hover responsive className="table-sm">
          <thead>
            <tr>
              <th>DATE</th>
              <th>DETAILS</th>
              <th>NOTES</th>
            </tr>
          </thead>
          <tbody>
            {previewResults.map((exp, idx) => (
              <tr key={idx}>
                <td>{new Date(exp.date).toLocaleDateString()}</td>
                <td dangerouslySetInnerHTML={{ __html: highlightText(exp.details) }} />
                <td dangerouslySetInnerHTML={{ __html: highlightText(exp.notes) }} />
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default ExpenseSearchReplaceScreen;